import { Plus, Trash } from 'lucide-react'
import { useRef } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { VALUE_TYPES } from '@/forms/VariantAxisForm/schema'
import { webalize } from '@/lib/catalog/webalize'

import { ParameterFormValues } from './schema'

export const ParameterForm = ({ isEdit = false }: { isEdit?: boolean }) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'catalog.parameters',
  })
  const { control, getValues, setValue } = useFormContext<ParameterFormValues>()
  // Saved codes are references, so a rename must not change them
  const codeManuallyEdited = useRef(isEdit)
  const valueCodeManuallyEdited = useRef<Record<number, boolean>>({})
  const isSurcharge = useWatch({ control, name: 'isSurcharge' })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'values',
  })

  const handleNameChange = (name: string) => {
    if (codeManuallyEdited.current) {
      return
    }

    setValue('code', webalize(name), {
      shouldValidate: Boolean(name),
    })
  }

  const handleValueLabelChange = (index: number, label: string) => {
    if (valueCodeManuallyEdited.current[index] || getValues(`values.${index}.id`)) {
      return
    }

    setValue(`values.${index}.code`, webalize(label), {
      shouldValidate: Boolean(label),
    })
  }

  return (
    <div className="space-y-4">
      <FormField
        control={control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('form.name.label')}</FormLabel>
            <FormControl>
              <Input
                placeholder={t('form.name.placeholder')}
                {...field}
                autoComplete="off"
                onChange={(event) => {
                  field.onChange(event)
                  handleNameChange(event.target.value)
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="code"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('form.code.label')}</FormLabel>
            <FormControl>
              <Input
                placeholder={t('form.code.placeholder')}
                {...field}
                autoComplete="off"
                onChange={(event) => {
                  codeManuallyEdited.current = true
                  field.onChange(event)
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="valueType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('form.valueType.label')}</FormLabel>
            <Select value={field.value} onValueChange={field.onChange}>
              <FormControl className="w-full">
                <SelectTrigger>
                  <SelectValue placeholder={t('form.valueType.placeholder')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {VALUE_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(`form.valueType.options.${type}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="space-y-2">
        <FormLabel>{t('form.roles.label')}</FormLabel>
        <FormField
          control={control}
          name="isDescriptive"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel className="font-normal">{t('form.roles.descriptive')}</FormLabel>
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="isFilterable"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel className="font-normal">{t('form.roles.filterable')}</FormLabel>
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="isSurcharge"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel className="font-normal">{t('form.roles.surcharge')}</FormLabel>
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="isDescriptive"
          render={() => (
            <FormItem>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <FormLabel>{t('form.values.title')}</FormLabel>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              append({
                code: '',
                label: '',
                percentOfBase: '',
                absolutePriceCzk: null,
              })
            }
          >
            <Plus className="size-4 mr-1" />
            {t('form.values.add')}
          </Button>
        </div>

        {fields.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('form.values.empty')}</p>
        )}

        {fields.map((item, index) => (
          <div key={item.id} className="space-y-2 rounded-md border p-3">
            <div className="flex gap-2 items-start">
              <FormField
                control={control}
                name={`values.${index}.label`}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input
                        placeholder={t('form.values.labelPlaceholder')}
                        {...field}
                        autoComplete="off"
                        onChange={(event) => {
                          field.onChange(event)
                          handleValueLabelChange(index, event.target.value)
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={`values.${index}.code`}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input
                        placeholder={t('form.values.codePlaceholder')}
                        {...field}
                        autoComplete="off"
                        onChange={(event) => {
                          valueCodeManuallyEdited.current[index] = true
                          field.onChange(event)
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-destructive hover:text-destructive"
                onClick={() => remove(index)}
              >
                <Trash className="size-4" />
              </Button>
            </div>

            {isSurcharge && (
              <div className="flex gap-2">
                <FormField
                  control={control}
                  name={`values.${index}.percentOfBase`}
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>{t('form.values.percentOfBase')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0"
                          {...field}
                          value={field.value ?? ''}
                          onChange={(event) => field.onChange(event.target.value)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`values.${index}.absolutePriceCzk`}
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>{t('form.values.absolutePriceCzk')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0"
                          value={
                            field.value === null ||
                            field.value === undefined ||
                            Number.isNaN(field.value)
                              ? ''
                              : field.value
                          }
                          onChange={(event) => {
                            const value = event.target.value
                            field.onChange(value === '' ? null : Number(value))
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
